import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Button } from '../Button';
import './TermsModal.scss';

// ─── TermsModal ───────────────────────────────────────────────────────────────
// Controlled modal displaying a title, body copy, and a single blue close button.

export const TermsModal = ({
  open,
  title = 'Terms and Conditions',
  bodyText = '',
  closeLabel = 'Close',
  onClose,
}) => {
  const closeRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKey = (e) => {
      if (e.key === 'Escape') typeof onClose === 'function' && onClose();
    };
    document.addEventListener('keydown', handleKey);
    closeRef.current?.focus();

    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener('keydown', handleKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="terms-modal__backdrop">
      {/* Focusable full-screen backdrop — closes modal on click */}
      <button
        type="button"
        className="terms-modal__backdrop-btn"
        aria-label="Close dialog"
        onClick={onClose}
        tabIndex={-1}
      />

      <div
        className="terms-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="terms-modal-title"
        aria-describedby="terms-modal-desc"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          e.stopPropagation();
          if (e.key === 'Escape') typeof onClose === 'function' && onClose();
        }}
      >
        {/* Close × button */}
        <button
          ref={closeRef}
          type="button"
          className="terms-modal__close"
          aria-label="Close dialog"
          onClick={onClose}
        >
          <img src="/images/close-x.svg" alt="" aria-hidden="true" />
        </button>

        <h2 id="terms-modal-title" className="terms-modal__title">
          {title}
        </h2>

        <p id="terms-modal-desc" className="terms-modal__body">
          {bodyText}
        </p>

        <div className="terms-modal__actions">
          <Button
            variant="primary"
            size="md"
            icon={null}
            onClick={onClose}
          >
            {closeLabel}
          </Button>
        </div>
      </div>
    </div>
  );
};

TermsModal.propTypes = {
  /** Whether the modal is visible */
  open: PropTypes.bool.isRequired,
  /** h2 heading displayed at the top of the modal */
  title: PropTypes.string,
  /** Body paragraph text */
  bodyText: PropTypes.string,
  /** Label for the close button */
  closeLabel: PropTypes.string,
  /** Called when the user closes the modal */
  onClose: PropTypes.func,
};

export default TermsModal;
